import React, { useState, useCallback, useEffect } from 'react';

const StepSituationLogement = ({ formData, onChange }) => {
  const [localError, setLocalError] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Options de situation de logement
  const situationOptions = [
    { value: 'proprietaire_avec_pret', label: 'Propriétaire avec prêt' },
    { value: 'proprietaire_sans_pret', label: 'Propriétaire sans prêt' },
    { value: 'locataire', label: 'Locataire' },
    { value: 'loge_famille', label: 'Logé(e) par la famille' },
    { value: 'loge_employeur', label: 'Logé(e) par l\'employeur' },
    { value: 'autre', label: 'Autre' }
  ];
  
  // Déterminer si des champs supplémentaires doivent être affichés
  const [showPretFields, setShowPretFields] = useState(formData.situationLogement === 'proprietaire_avec_pret');
  const [showAutreField, setShowAutreField] = useState(formData.situationLogement === 'autre');

  // Validation locale - ne s'affiche que si on a tenté de soumettre
  const validateFields = useCallback(() => {
    if (!formData.situationLogement) {
      setLocalError('Veuillez sélectionner votre situation de logement');
      return false;
    } else if (!formData.dateEntreeLogement) {
      setLocalError('Veuillez indiquer la date d\'entrée dans votre logement');
      return false;
    } else if (formData.situationLogement === 'autre' && !formData.situationLogementAutre) {
      setLocalError('Veuillez préciser votre situation de logement');
      return false;
    } else if (formData.situationLogement === 'proprietaire_avec_pret') {
      if (!formData.organismePreteur) {
        setLocalError('Veuillez indiquer l\'organisme prêteur');
        return false;
      } else if (!formData.dateFinPret) {
        setLocalError('Veuillez indiquer la date de fin du prêt');
        return false;
      }
    }
    
    setLocalError('');
    return true;
  }, [formData.situationLogement, formData.dateEntreeLogement, formData.situationLogementAutre, formData.organismePreteur, formData.dateFinPret]);

  // Gestionnaire de changement spécifique pour les boutons
  const handleButtonChange = (fieldName, value) => {
    onChange({
      target: {
        name: fieldName,
        value: value
      }
    });
    
    // Mettre à jour l'affichage des champs supplémentaires
    if (fieldName === 'situationLogement') {
      setShowPretFields(value === 'proprietaire_avec_pret');
      setShowAutreField(value === 'autre');
    }

    // Vérifier après chaque changement si on a déjà essayé de soumettre
    if (attemptedSubmit) {
      validateFields();
    }
  };

  // Gestionnaire de changement pour les autres champs
  const handleChange = (e) => {
    onChange(e);
    
    // Vérifier après chaque changement si on a déjà essayé de soumettre
    if (attemptedSubmit) {
      validateFields();
    }
  };

  // Mettre à jour l'affichage des champs supplémentaires lors du chargement initial
  useEffect(() => {
    setShowPretFields(formData.situationLogement === 'proprietaire_avec_pret');
    setShowAutreField(formData.situationLogement === 'autre');
  }, [formData.situationLogement]);

  // Simule le comportement du bouton suivant pour valider les champs
  React.useEffect(() => {
    // Ajouter un écouteur d'événement pour le bouton suivant global
    const handleNextClick = () => {
      setAttemptedSubmit(true);
      validateFields();
    };
    
    // Trouver le bouton "Suivant" dans le formulaire parent
    const nextButton = document.querySelector('button[type="button"]:not([class*="retour"]):not([disabled])');
    if (nextButton) {
      nextButton.addEventListener('click', handleNextClick);
    }
    
    return () => {
      // Nettoyage
      if (nextButton) {
        nextButton.removeEventListener('click', handleNextClick);
      }
    };
  }, [validateFields]); // Ajout de validateFields comme dépendance

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-orpi-secondary">
        Dites-nous en plus sur votre logement
      </h2>

      {attemptedSubmit && localError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {localError}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">Mode d'habitation</label>
          <div className="grid grid-cols-2 gap-4">
            {situationOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`p-3 border rounded-lg hover:border-orpi-primary ${
                  formData.situationLogement === option.value 
                    ? 'border-orpi-primary bg-orpi-light' 
                    : ''
                }`}
                onClick={() => handleButtonChange('situationLogement', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Champ supplémentaire pour "Autre" */}
        {showAutreField && (
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">
              Précisez votre situation de logement <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="situationLogementAutre"
              value={formData.situationLogementAutre || ''}
              onChange={handleChange}
              className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
              placeholder="Ex: Hébergé gratuitement, résidence secondaire, etc."
              required
            />
          </div>
        )}
        
        {/* Champs supplémentaires pour "Propriétaire avec prêt" */}
        {showPretFields && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-orpi-secondary">
                Organisme prêteur <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="organismePreteur"
                value={formData.organismePreteur || ''}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
                placeholder="Nom de votre banque ou organisme prêteur"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1 text-orpi-secondary">
                Date de fin du prêt <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="dateFinPret"
                value={formData.dateFinPret || ''}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
                required
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Dans votre logement actuel depuis
          </label>
          <input
            type="date"
            name="dateEntreeLogement"
            value={formData.dateEntreeLogement || ''}
            onChange={handleChange}
            className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
            required
          />
        </div>
      </div>
      
      <p className="text-sm text-gray-500 mt-4">
        <span className="text-red-500">*</span> Champs obligatoires
      </p>
    </div>
  );
};

export default StepSituationLogement;